import React, {ReactNode} from 'react'

import {SwipePanes} from '@vio/atlas/helpers/SwipePanes'
import {Image, SIZES, SizeType} from '@vio/atlas/Image'
import {getDevicePixelRatio} from '@vio/core/src/_web/utils/browser/getDevicePixelRatio'
import {ImageProvider} from '@vio/core/src/staticContent/types/ImageProvider'

import {ImageCenteredWrapper, Wrapper} from './styled'

export type ImageErrorHandler = (payload: {
  originalImageURL: string
  imagePosition: number
  imageProxyURL: string
}) => void

type PropTypes = {
  /** List of images to be displayed */
  images: string[]
  /** Size to scale images to */
  imageSize?: SizeType
  /** Imaging service for resizing */
  imageProvider: ImageProvider
  /** First pane to display after mount */
  initialImageIndex?: number
  /** Displays dots indicator with shaded gradient */
  hasDarkGradient?: boolean
  /** Displays page number with shade background underneath */
  hasDarkBackground?: boolean
  /** How to scale the image */
  backgroundSize: 'cover' | 'contain'
  /** Show pane counter in the bottom right */
  showPageNumber?: boolean
  /** Show next/previous pane buttons */
  showPaginationButtons?: boolean
  /** Whether to enable pinch-zoom */
  isZoomEnabled?: boolean
  /** Whether we're in an RTL language */
  isRtl: boolean
  /** Triggered whenever the user swipes the gallery */
  onSwipe?: (index: number) => void
  /** Triggered whenever the user zooms the gallery */
  onZoom?: () => void
  /** Handle overlay close */
  onClose?: () => void
  /** Image placeholder in case of broken images */
  placeHolder?: ReactNode
  /** Fallback for handling error on image */
  handleImageError?: ImageErrorHandler
  /** Shows pagination buttons when pane is hovered */
  showPaginationOnHover?: boolean
  /** Triggered whenever the user clicks on an image */
  onImageClick?: () => void
  /** Whether to enable arrow keys */
  arrowKeysEnabled?: boolean
}

const makeRenderImagePane = (
  images: string[],
  imageProvider: ImageProvider,
  size: SizeType,
  backgroundSize: 'contain' | 'cover',
  placeHolder?: ReactNode,
  onImageError?: ImageErrorHandler
) =>
  function renderImagePage(index: number) {
    const imageUrl = images?.[index]
    if (!imageUrl) return null
    const payload = {
      originalImageURL: imageUrl,
      imagePosition: index + 1,
      imageProxyURL: imageProvider(imageUrl, SIZES[size], {
        dpr: getDevicePixelRatio()
      })
    }
    const handleImageError = () => onImageError?.(payload)

    return (
      <ImageCenteredWrapper>
        <Image
          fullWidth
          isResponsive
          imageProvider={imageProvider}
          size={size}
          url={imageUrl}
          backgroundSize={backgroundSize}
          placeHolder={placeHolder}
          hasSkeletonLoader
          onImageError={handleImageError}
          fetchPriority={index === 0 ? 'high' : 'auto'}
          loading={index === 0 ? 'eager' : 'lazy'}
          draggable={false}
        />
      </ImageCenteredWrapper>
    )
  }

export const SwipeGallery = ({
  images,
  imageProvider,
  initialImageIndex = 0,
  imageSize = 'gridPrimary',
  showPageNumber = false,
  hasDarkGradient = true,
  hasDarkBackground = false,
  showPaginationButtons = false,
  isZoomEnabled = true,
  isRtl,
  backgroundSize,
  onSwipe,
  onZoom,
  onClose = () => null,
  placeHolder,
  handleImageError,
  showPaginationOnHover = false,
  onImageClick,
  arrowKeysEnabled = true
}: PropTypes) => {
  if (!images?.length) return null

  return (
    <Wrapper>
      <SwipePanes
        panesAmount={images?.length}
        renderPane={makeRenderImagePane(
          images,
          imageProvider,
          imageSize,
          backgroundSize,
          placeHolder,
          handleImageError
        )}
        initialPaneIndex={initialImageIndex}
        showPageNumber={showPageNumber}
        hasDarkGradient={hasDarkGradient}
        hasDarkBackground={hasDarkBackground}
        showPaginationButtons={showPaginationButtons}
        isZoomEnabled={isZoomEnabled}
        isRtl={isRtl}
        onSwipe={onSwipe}
        onZoom={onZoom}
        onClose={onClose}
        showPaginationOnHover={showPaginationOnHover}
        onImageClick={onImageClick}
        arrowKeysEnabled={arrowKeysEnabled}
      />
    </Wrapper>
  )
}
